import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { AlertTriangle, FolderTree } from "lucide-react";
import { IDuplicateMemberError } from "../interfaces/duplicate-member-error.interface";

interface IDuplicateMembersDialog {
  error: IDuplicateMemberError;
  onClose: () => void;
}

const formatLocation = (levelName: string, subLevelName: string, shopDrawingName: string) =>
  [levelName, subLevelName, shopDrawingName].filter(Boolean).join("  >  ");

export const DuplicateMembersDialog: React.FC<IDuplicateMembersDialog> = ({
  error,
  onClose,
}) => {
  const total = error.conflicts.reduce((sum, c) => sum + c.members.length, 0);

  return (
    <Dialog title="Import blocked: duplicate member names" width={"560px"} onClose={onClose}>
      <div className="flex gap-3 mb-4">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500 mt-0.5" strokeWidth={1.5} />
        <p className="text-sm">
          {total} member name{total === 1 ? "" : "s"} in this file already exist
          {total === 1 ? "s" : ""} in the project. Member names must be unique across
          the whole project.
        </p>
      </div>

      <div className="max-h-[50vh] overflow-y-auto flex flex-col gap-3">
        {error.conflicts.map((conflict, idx) => (
          <div key={idx} className="border border-neutral-30 rounded p-3">
            <div className="flex items-center gap-2 text-sm font-medium mb-2">
              <FolderTree className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              <span>
                Already in:{" "}
                {formatLocation(conflict.levelName, conflict.subLevelName, conflict.shopDrawingName) ||
                  "Unknown location"}
              </span>
            </div>
            <ul className="list-disc pl-8 text-sm space-y-0.5">
              {conflict.members.map((name) => (
                <li key={name} className="break-words">{name}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="text-sm mt-4">
        <p className="font-medium mb-1">To fix this, either:</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>rename or remove these members in the Excel file and import again, or</li>
          <li>check the location(s) above and remove the existing members if they are wrong.</li>
        </ul>
      </div>

      <DialogActionsBar>
        <div className="flex justify-end">
          <Button themeColor={"primary"} className="iconcta" onClick={onClose}>
            OK
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};
